import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { getTimeoutForToast } from '@/utils/toast';
import { DEFAULT_TOAST_TIMEOUT, PERMANENT_TOAST_INTERVAL } from '@/constants/Toast';
import { useSystemConfigurationStore } from '@/store/useSystemConfigurationStore';

export interface ToastOptions {
  x?: string;
  y?: string;
  color?: string;
  icon?: string;
  iconColor?: string;
  classes?: string | object | Array<object | string>;
  timeout?: number;
  dismissable?: boolean;
  multiLine?: boolean;
  vertical?: boolean;
  showClose?: boolean;
  closeText?: string;
  closeIcon?: string;
  closeColor?: string;
  queueable?: boolean;
}

export interface Toast {
  message: string;
  options?: ToastOptions;
}

export const useToastStore = defineStore('toast', () => {
  const systemConfigurationStore = useSystemConfigurationStore();

  const toasts = ref<Toast[]>([]);

  const toast = computed(() => {
    return toasts.value.length ? toasts.value[0] : null;
  });

  const create = (toast: Toast) => {
    if (!toast.options?.timeout) {
      toast.options = {
        ...toast.options,
        timeout: getTimeoutForToast(toast.message),
      };
    }

    if (toast.options?.timeout === PERMANENT_TOAST_INTERVAL) {
      toast.options = {
        ...toast.options,
        showClose: true,
      };
    }

    // allow only close toast by button and ignore previous option
    if (systemConfigurationStore.isCloseToastUsingButtonAllowed) {
      toast.options = {
        ...toast.options,
        showClose: true,
        timeout: DEFAULT_TOAST_TIMEOUT,
      };
    }

    if (toast.options?.queueable) {
      toasts.value.push(toast);
    } else {
      toasts.value = [toast];
    }
  };

  const success = (params: { message: string; options?: ToastOptions }) => {
    const opt = params.options ? { ...params.options, color: 'success' } : { color: 'success' };

    create({
      message: params.message,
      options: opt,
    });
  };

  const error = (params: { message: string; options?: ToastOptions }) => {
    const opt = params.options ? { ...params.options, color: 'error' } : { color: 'error' };

    create({
      message: params.message,
      options: opt,
    });
  };

  const showErrorToast = (params: { message: string; options?: ToastOptions }) => {
    error(params);
  };

  const info = (params: { message: string; options?: ToastOptions }) => {
    const opt = params.options ? { ...params.options, color: 'info' } : { color: 'info' };

    create({
      message: params.message,
      options: opt,
    });
  };

  const clearQueue = () => {
    toasts.value = [];
  };

  const hide = () => {
    toasts.value.shift();
  };

  return {
    toasts,
    toast,

    create,
    success,
    error,
    showErrorToast,
    info,
    clearQueue,
    hide,
  };
});
